<script setup lang="ts">
import { useRafFn, useResizeObserver } from '@vueuse/core';
import { onMounted, ref, useTemplateRef, watch } from 'vue';
import DTooltip from '#layers/ui/components/Tooltip.vue';

interface Props {
	text: string;
}

const { text } = defineProps<Props>();

const contentRef = useTemplateRef<HTMLElement>('content');
const isTruncated = ref(false);

const OVERFLOW_TOLERANCE_PX = 1;
const tooltipUi = {
	content: 'h-auto max-w-100 max-h-60 items-start overflow-auto select-text',
	text: 'w-full min-w-0 overflow-visible text-clip whitespace-pre-wrap break-words',
};

function measure() {
	const element = contentRef.value;
	if (!element) return;

	isTruncated.value = element.scrollWidth - element.clientWidth > OVERFLOW_TOLERANCE_PX;
}

const { resume: scheduleMeasure } = useRafFn(measure, { immediate: false, once: true });

onMounted(() => {
	scheduleMeasure();
	// Web fonts settle their intrinsic width after mount, so re-measure once they land.
	document.fonts?.ready.then(scheduleMeasure);
});

watch(() => text, () => scheduleMeasure());

useResizeObserver(contentRef, scheduleMeasure);
</script>

<template>
	<DTooltip :text :ui="tooltipUi" :disabled="!isTruncated || !text">
		<span
			ref="content"
			class="block truncate"
			:tabindex="isTruncated && text ? 0 : undefined"
		>
			{{ text }}
		</span>
	</DTooltip>
</template>
